import { useQuery } from '@tanstack/react-query'
import { Cpu, Lock, Info } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import type { StructureData, GpoParams } from '../types.ts'
import { Badge, Button, Card, Checkbox, EmptyState, Input, LoadingState } from '@/components/ui'

interface GpoParameter {
  key: string
  label: string
  type: 'int' | 'bool'
  default: number | boolean
  min?: number
  max?: number
  unit?: string
  description?: string
}

interface StandardGpo {
  key: string
  nameTemplate: string
  description: string
  linkOu: string
  enforced?: boolean
  parameters: GpoParameter[]
}

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
}

export function GposTab({ data, update }: Props) {
  const { data: gposData, isLoading } = useQuery<{ gpos: StandardGpo[] }>({
    queryKey: ['it-gpo-standards'],
    queryFn: () => apiClient.get('/it-structures/gpo-standards').then((r) => r.data),
  })
  const gpos = gposData?.gpos ?? []
  const customerParams: GpoParams = data.gpoParams ?? {}

  function setParam(gpoKey: string, paramKey: string, value: number | boolean) {
    const next: GpoParams = {
      ...customerParams,
      [gpoKey]: {
        ...(customerParams[gpoKey] ?? {}),
        [paramKey]: value,
      },
    }
    update({ gpoParams: next })
  }

  function resetParam(gpoKey: string, paramKey: string) {
    const gpoEntry = { ...(customerParams[gpoKey] ?? {}) }
    delete gpoEntry[paramKey]
    const next: GpoParams = { ...customerParams }
    if (Object.keys(gpoEntry).length === 0) delete next[gpoKey]
    else next[gpoKey] = gpoEntry
    update({ gpoParams: next })
  }

  function getValue(gpo: StandardGpo, param: GpoParameter): number | boolean {
    const v = customerParams[gpo.key]?.[param.key]
    return v !== undefined ? v : param.default
  }

  function isOverridden(gpoKey: string, paramKey: string): boolean {
    return customerParams[gpoKey]?.[paramKey] !== undefined
  }

  if (isLoading) {
    return <LoadingState label="Lade Standard-GPOs…" />
  }

  if (gpos.length === 0) {
    return (
      <EmptyState
        title="Keine Standard-GPOs definiert"
        description="Im Code sind noch keine Standard-GPOs hinterlegt."
      />
    )
  }

  const prefix = data.domainPrefix || 'PREFIX'

  return (
    <div className="space-y-4">
      <Card className="border-blue-100 bg-blue-50">
        <div className="px-3 py-2 text-caption text-blue-700 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Die GPO-Struktur (welche Policies, welche Registry-Keys) ist fest im Code definiert.
            Hier können pro Kunde die Werte (z.B. Mindestlängen, Timeouts) angepasst werden.
            Nicht gesetzte Werte nutzen den Default.
          </span>
        </div>
      </Card>

      {gpos.map((gpo) => (
        <Card key={gpo.key}>
          <Card.Header className="bg-gray-50 flex-wrap">
            <div className="flex items-baseline gap-2 flex-wrap">
              <Cpu className="w-4 h-4 text-rose-500" />
              <span className="text-body font-semibold text-gray-900 font-mono">
                {gpo.nameTemplate.replace('{PREFIX}', prefix)}
              </span>
              <span className="text-caption bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">
                → {gpo.linkOu.replace('{PREFIX}', prefix)}
              </span>
              {gpo.enforced && (
                <Badge variant="warning" size="sm">enforced</Badge>
              )}
            </div>
          </Card.Header>
          <div className="px-4 py-2 border-b border-gray-100 text-caption text-gray-500">
            {gpo.description}
          </div>

          <div className="divide-y divide-gray-100">
            {gpo.parameters.map((param) => {
              const value = getValue(gpo, param)
              const overridden = isOverridden(gpo.key, param.key)
              return (
                <div key={param.key} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <label className="text-body text-gray-900">{param.label}</label>
                      {overridden ? (
                        <Badge variant="brand" size="sm">kunden­spezifisch</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">
                          <Lock className="w-2.5 h-2.5" />
                          Default
                        </Badge>
                      )}
                    </div>
                    {param.description && (
                      <p className="text-caption text-gray-400 mt-0.5">{param.description}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {param.type === 'int' ? (
                      <>
                        <Input
                          type="number"
                          value={value as number}
                          min={param.min}
                          max={param.max}
                          onChange={(e) => {
                            const n = Number(e.target.value)
                            if (Number.isFinite(n)) setParam(gpo.key, param.key, n)
                          }}
                          wrapperClassName="w-24"
                          className="font-mono text-right"
                        />
                        {param.unit && <span className="text-caption text-gray-500 w-20">{param.unit}</span>}
                      </>
                    ) : (
                      <Checkbox
                        checked={value as boolean}
                        onChange={(e) => setParam(gpo.key, param.key, e.target.checked)}
                      />
                    )}

                    {overridden && (
                      <Button
                        variant="link"
                        size="xs"
                        title="Auf Default zurücksetzen"
                        onClick={() => resetParam(gpo.key, param.key)}
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      ))}
    </div>
  )
}
